// app/javascript/controllers/album_navigation_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["item", "timeline"]
    static values = {
        selectedId: Number
    }

    connect() {
        this._paused = false;
        this._scrollTimeout = null;
        this._isUserScrolling = false;
        this._lastFrame = null;
        this._hovered = false;

        this._onEnter = () => { this._hovered = true; this._paused = true; };
        this._onLeave = () => { this._hovered = false; this._paused = this._isUserScrolling; };

        this._onScroll = () => {
            // Automatic animation must not trigger storage writes and layout reads.
            if (this._paused || this._isUserScrolling) {
                clearTimeout(this._saveTimeout);
                this._saveTimeout = setTimeout(() => this.saveScrollPosition(), 200);
            }
            if (this._isUserScrolling) {
                this._onTouchStart();
            }
        };

        this._onTouchStart = () => {
            this._isUserScrolling = true;
            this._paused = true;
            clearTimeout(this._scrollTimeout);
            this._scrollTimeout = setTimeout(() => {
                this._paused = this._hovered;
                this._isUserScrolling = false;
            }, 200);
        };

        this.timelineTarget.addEventListener("mouseenter", this._onEnter);
        this.timelineTarget.addEventListener("mouseleave", this._onLeave);

        this.timelineTarget.addEventListener("touchstart", this._onTouchStart, { passive: true });
        this.timelineTarget.addEventListener("wheel", this._onTouchStart, { passive: true });
        this.timelineTarget.addEventListener("scroll", this._onScroll, { passive: true });

        this.restoreScrollPosition()
        this._animate = this.animate.bind(this);
        this._animationFrame = window.requestAnimationFrame(this._animate);
    }

    animate(timestamp) {
        const elapsed = this._lastFrame === null ? 0 : Math.min(timestamp - this._lastFrame, 50);
        this._lastFrame = timestamp;
        if (!this._paused && this.hasTimelineTarget && elapsed > 0) {
            const timeline = this.timelineTarget;
            if (timeline.scrollWidth > timeline.clientWidth) {
                const left = timeline.scrollLeft + timeline.clientWidth >= timeline.scrollWidth - 1
                    ? 0 : timeline.scrollLeft + elapsed * 0.08;
                timeline.scrollTo({ left, behavior: 'instant' });
            }
        }
        this._animationFrame = window.requestAnimationFrame(this._animate);
    }

    disconnect() {
        this._paused = true;
        clearTimeout(this._scrollTimeout);
        clearTimeout(this._saveTimeout);
        window.cancelAnimationFrame(this._animationFrame);

        this.timelineTarget.removeEventListener("mouseenter", this._onEnter);
        this.timelineTarget.removeEventListener("mouseleave", this._onLeave);
        this.timelineTarget.removeEventListener("touchstart", this._onTouchStart);
        this.timelineTarget.removeEventListener("wheel", this._onTouchStart);
        this.timelineTarget.removeEventListener("scroll", this._onScroll);

    }

    saveScrollPosition() {
        const scrollLeft = this.element.scrollLeft
        localStorage.setItem('albumNavScrollPosition', scrollLeft)

        const leftmostItem = this.getLeftmostVisibleItem()
        if (leftmostItem) {
            const itemId = leftmostItem.dataset.itemId
            localStorage.setItem('albumNavLeftmostItem', itemId)
        }
    }

    restoreScrollPosition() {
        const leftmostItemId = localStorage.getItem('albumNavLeftmostItem')
        if (leftmostItemId) {
            const targetItem = this.itemTargets.find(item =>
                item.dataset.itemId === leftmostItemId
            )
            if (targetItem) {
                this.scrollToItem(targetItem)
                return
            }
        }

        const savedScrollLeft = localStorage.getItem('albumNavScrollPosition')
        if (savedScrollLeft) {
            this.element.scrollLeft = parseInt(savedScrollLeft)
        }
    }

    scrollToItem(item) {
        const containerRect = this.element.getBoundingClientRect()
        const itemRect = item.getBoundingClientRect()
        const scrollLeft = itemRect.left - containerRect.left + this.element.scrollLeft

        this.element.scrollTo({
            left: scrollLeft,
            behavior: 'smooth'
        })
    }

    getLeftmostVisibleItem() {
        const containerRect = this.element.getBoundingClientRect()

        for (const item of this.itemTargets) {
            const itemRect = item.getBoundingClientRect()
            if (itemRect.right > containerRect.left && itemRect.left <= containerRect.left + 50) {
                return item
            }
        }

        return this.itemTargets.find(item => {
            const itemRect = item.getBoundingClientRect()
            return itemRect.right > containerRect.left && itemRect.left < containerRect.right
        })
    }

    scrollToSelected() {
        const selectedItem = this.itemTargets.find(item =>
            item.dataset.itemId === this.selectedIdValue.toString()
        )
        if (selectedItem) {
            this.scrollToItem(selectedItem)
        }
    }
}
